import { differenceInDays } from 'date-fns';
import * as webStorage from '../webStorage';
import * as cookies from '../utils/cookies';

import piwik from '../services/analytics';
import { eventTags } from '../services/analytics/tags';
import { getSession } from '../selectors/global';
import {
  getRentalRegistrationPhoneNumber,
  getRentalRegistrationPlateNumber,
  getRentalRegistrationVinNumber,
} from '../selectors/rentalMission';
import { getSettings } from '../selectors/settings';
import { getClaim } from '../selectors/coreApi';
import {
  RENTAL_CLAIM_LOADED,
  NO_CLAIM_FOUND,
  searchClaim,
  RENTAL_MISSION_CANCELED,
  RENTAL_EXTENSION_CANCELED,
  RENTAL_EXTENSION_SENT,
  VIEW_TIMELINE,
} from './coreApi';

export const SET_REGISTRATION_VALUES = 'DRSA/RENTAL_MISSION/RENTAL_REGISTRATION/SET_REGISTRATION_VALUES';
export const SET_RENTAL_DRIVER_DETAILS = 'DRSA/RENTAL_MISSION/RENTAL_DRIVER/SET_RENTAL_DRIVER_DETAILS';
export const SET_VEHICLE_INFORMATION_DETAILS = 'DRSA/RENTAL_MISSION/RENTAL_INFORMATION/SET_VEHICLE_INFORMATION_DETAILS';
export const VEHICLE_GARAGE_LOCATION_CORRECT = 'DRSA/RENTAL_MISSION/VEHICLE_GARAGE_LOCATION_CORRECT';
export const VEHICLE_LOCATION = 'DRSA/RENTAL_MISSION/VEHICLE_LOCATION';

export const EXTENSION_DURATION = 'DRSA/RENTAL_MISSION/RENTAL_EXTENSION/EXTENSION_DURATION';
export const EXTENSION_DURATION_WEEKEND = 'DRSA/RENTAL_MISSION/RENTAL_EXTENSION/EXTENSION_DURATION_WEEKEND';
export const initialState = {};

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SET_REGISTRATION_VALUES:
      return {
        ...state,
        rentalRegistration: action.payload,
      };
    case SET_RENTAL_DRIVER_DETAILS:
      return {
        ...state,
        rentalDrivers: action.payload?.data,
      };
    case SET_VEHICLE_INFORMATION_DETAILS: {
      const {
        rentalBookingDetails: { from, durationInMinutes },
        pickUpLocation,
        dropOffLocation,
        rentalCategory,
      } = action.payload;
      return {
        ...state,
        rentalVehicleDetails: { rentalCategory, pickUpLocation, dropOffLocation },
        rentalBookingDetails: {
          from,
          durationInMinutes,
        },
      };
    }
    case VEHICLE_LOCATION:
      return {
        ...state,
        rentalVehicleLocation: action.payload,
      };
    case RENTAL_MISSION_CANCELED:
    case RENTAL_EXTENSION_CANCELED:
    case VIEW_TIMELINE:
      return {};
    case RENTAL_EXTENSION_SENT:
      return {
        ...state,
        disableRentalExtensionSummary: true,
      };
    case EXTENSION_DURATION:
      return {
        ...state,
        rentalExtensionDurationInMinutes: action.payload,
      };
    default:
      return state;
  }
};

export const storeRegistrationData = ({ payload }) => ({ type: SET_REGISTRATION_VALUES, payload });
export const storeRentalDriver = ({ payload }) => ({ type: SET_RENTAL_DRIVER_DETAILS, payload });
export const storeVehicleInformation = ({ payload }) => ({ type: SET_VEHICLE_INFORMATION_DETAILS, payload });
export const storeVehicleLocation = ({ payload }) => ({ type: VEHICLE_LOCATION, payload });

export const storeRentalExtensionDuration = ({ payload }) => ({ type: EXTENSION_DURATION, payload });
export const rentalExtensionDurationWeekend = () => ({ type: EXTENSION_DURATION_WEEKEND });

export const setRentalRegistrationDone = () => (_, getState) => {
  const state = getState();
  const session = getSession(state);
  cookies.setRentalRegistrationDone(session);
};

export const loadClaim = () => (dispatch, getState) => {
  const state = getState();
  const settings = getSettings(state);
  let session = getSession(state);

  const shouldSendPlateNumber = settings?.RENTAL_WORKFLOW?.RENTAL_CAR_PLATE_DISPLAYED;
  const shouldSendVinNumber = settings?.RENTAL_WORKFLOW?.RENTAL_CAR_VIN_DISPLAYED;

  const plateNumber = shouldSendPlateNumber ? getRentalRegistrationPlateNumber(state) : null;
  const vin = shouldSendVinNumber ? getRentalRegistrationVinNumber(state) : null;
  const phoneNumber = getRentalRegistrationPhoneNumber(state);

  return dispatch(searchClaim(plateNumber, vin, phoneNumber)).then(claims => {
    if (!claims.numberOfElements) {
      piwik.trackEvent(eventTags.rental_registration_no_claim);
      return dispatch({ type: NO_CLAIM_FOUND });
    }

    const claim = claims.content[0];
    if (differenceInDays(new Date(), new Date(claim.creationDate)) > 7) {
      piwik.trackEvent(eventTags.rental_registration_exceeded_seven_days);
      return dispatch({ type: NO_CLAIM_FOUND });
    }

    dispatch({ type: RENTAL_CLAIM_LOADED, claim });

    const updatedState = getState();
    const newClaim = getClaim(updatedState);
    session = getSession(updatedState);
    webStorage.setClaimId(session, newClaim);

    return claim;
  });
};

const actions = {
  storeRegistrationData,
  storeRentalDriver,
  storeVehicleInformation,
  loadClaim,
  storeVehicleLocation,
  storeRentalExtensionDuration,
  rentalExtensionDurationWeekend,
  setRentalRegistrationDone,
};

export default { reducer, actions };
